import { SidebarBadgeTone } from './menuConfig'
import { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Badge } from 'components/ds2/Badge'
import { handleLinkClick } from 'utils/linkClick'

interface SidebarChildItemProps {
    label: string
    active?: boolean
    /**
     * Disabled rows are visually muted and reject clicks on the row itself,
     * but their `suffix` slot stays interactive — used for paywalled items
     * where the row is locked and the suffix carries an "Enable" CTA.
     */
    disabled?: boolean
    /** Optional badge shown to the right of the label (BETA / NEW / count). */
    badge?: { label: string; tone: SidebarBadgeTone }
    /** Optional content rendered to the very right (e.g. an inline button). */
    suffix?: ReactNode
    /** Stable DOM hook used by overlays/tooltips to anchor to a specific row. */
    sidebarItemId?: string
    /** Real destination. When set (and the row is not disabled) the row renders as a link, so "open in new tab" works. */
    href?: string
    onClick?: () => void
}

/**
 * A sub-row rendered inside an expanded `SidebarGroup` (or the collapsed-rail
 * popover). Mirrors the reference design's `NavChild`
 * (`new-agent-numeo-main/src/App.tsx` lines 388–413):
 *
 * - Label only — no icon. Icons are reserved for the group header or for
 *   top-level flat rows; nesting an icon inside an already-indented child
 *   would duplicate the visual hierarchy.
 * - Active state is signalled by a 2 px accent-blue left bar plus an
 *   accent-soft background fill.
 * - Hover background moves towards `--numeo-surface` (`bg-white` /
 *   `dark:bg-slate-900`), matching `ROW_IDLE`.
 */
export const SidebarChildItem = ({ label, active, disabled, badge, suffix, sidebarItemId, href, onClick }: SidebarChildItemProps) => {
    const rowClass = [
        // `flex-1` (not `w-full` + `shrink-0`) — this button shares its flex row with the
        // 2px accent bar sibling above. `w-full` sets a 100%-of-container basis, which with
        // `shrink-0` can't yield room for the bar and overflows by the bar's width, clipping
        // the rounded right corner against whatever ancestor clips overflow. `flex-1` starts
        // from a 0 basis and grows to fill exactly the space left after the bar — no overflow.
        'ml-1 flex h-8 flex-1 items-center gap-2 rounded-xl pl-3 pr-2 text-sm font-medium transition-colors',
        disabled
            ? 'cursor-not-allowed text-foreground-muted opacity-50'
            : active
              ? 'cursor-pointer bg-accent-soft text-foreground'
              : 'cursor-pointer text-foreground hover:bg-black/5 dark:hover:bg-white/10',
    ].join(' ')
    const content = (
        <>
            <span className="min-w-0 flex-1 truncate text-left">{label}</span>
            {/* Badge is suppressed when disabled so the Enable CTA in `suffix` carries the row's only call to action. */}
            {!disabled && badge && <Badge variant="soft" type={badge.tone} size="sm" label={badge.label} />}
            {suffix && <span className="flex shrink-0 items-center">{suffix}</span>}
        </>
    )
    return (
        <div className="flex shrink-0 items-stretch">
            {/* `-ml-px` lives on the bar alone (not the row) so only the LEFT edge overlaps the
            parent's `border-l` connector line by 1px — shifting the whole row left instead
            pushed the button's right edge 1px past the nav's `overflow-x-hidden` boundary and
            clipped its rounded corner square. */}
            <div className={`-ml-px w-[2px] shrink-0 ${active ? 'bg-accent' : 'bg-transparent'}`} />
            {href && !disabled ? (
                <Link
                    to={href}
                    onClick={(e) => handleLinkClick(e, onClick)}
                    aria-current={active ? 'page' : undefined}
                    data-sidebar-item={sidebarItemId}
                    className={`${rowClass} no-underline`}
                >
                    {content}
                </Link>
            ) : (
                <div
                    role="button"
                    tabIndex={disabled ? -1 : 0}
                    onClick={disabled ? undefined : onClick}
                    onKeyDown={disabled ? undefined : (e) => (e.key === 'Enter' || e.key === ' ') && onClick?.()}
                    aria-current={active ? 'page' : undefined}
                    aria-disabled={disabled || undefined}
                    data-sidebar-item={sidebarItemId}
                    className={rowClass}
                >
                    {content}
                </div>
            )}
        </div>
    )
}
